if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.activeMediaId = this.elements.viewer?.querySelector('.is-active')?.dataset.mediaId;
        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 120));
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.elements.thumbnails.addEventListener('keydown', this.onThumbnailKeyDown.bind(this));
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (this.dataset.nlGallery !== 'true' || !this.elements.viewer) return;
        const rail = this.elements.viewer.querySelector('.product__media-list');
        if (!rail) return;
        this.galleryControlController?.abort();
        this.galleryControlController = new AbortController();
        rail.addEventListener('scroll', () => this.syncViewerControls(), { signal: this.galleryControlController.signal });
        this.galleryControlObserver?.disconnect();
        this.galleryControlObserver = new ResizeObserver(() => this.syncViewerControls());
        this.galleryControlObserver.observe(rail);
        this.syncViewerControls();
      }

      disconnectedCallback() {
        this.galleryControlController?.abort();
        this.galleryControlObserver?.disconnect();
      }

      syncViewerControls() {
        const rail = this.elements.viewer.querySelector('.product__media-list');
        const slides = [...rail.children].filter((slide) => slide.hasAttribute('data-media-id') && slide.clientWidth > 0);
        if (!slides.length) return;
        // This rail deliberately shows one whole image, unlike Dawn's peek grid.
        // Count rendered slides, and tolerate subpixel rounding at either end.
        const left = Math.abs(rail.scrollLeft);
        const index = slides.reduce((closest, slide, i) =>
          Math.abs(Math.abs(slide.offsetLeft - slides[0].offsetLeft) - left) <
          Math.abs(Math.abs(slides[closest].offsetLeft - slides[0].offsetLeft) - left) ? i : closest, 0);
        const current = this.elements.viewer.querySelector('.slider-counter--current');
        const total = this.elements.viewer.querySelector('.slider-counter--total');
        if (current) current.textContent = index + 1;
        if (total) total.textContent = slides.length;
        this.elements.viewer.querySelector('button[name="previous"]')?.toggleAttribute('disabled', left <= 1);
        this.elements.viewer.querySelector('button[name="next"]')?.toggleAttribute('disabled', left >= rail.scrollWidth - rail.clientWidth - 1);
      }

      onSlideChanged(event) {
        const activeMedia = event.detail?.currentElement;
        if (!activeMedia) return;
        if (this.activeMediaId !== activeMedia.dataset.mediaId) {
          window.pauseAllMedia();
          this.activeMediaId = activeMedia.dataset.mediaId;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeMedia);
        });
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.announceLiveRegion(activeMedia, event.detail.currentPage);
      }

      onThumbnailKeyDown(event) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const currentButton = event.target.closest('.thumbnail');
        if (!currentButton) return;
        const buttons = [...this.elements.thumbnails.querySelectorAll('.thumbnail')].filter((button) => button.getClientRects().length);
        const currentIndex = buttons.indexOf(currentButton);
        if (currentIndex < 0) return;
        event.preventDefault();
        const rtl = getComputedStyle(this.elements.thumbnails).direction === 'rtl';
        const direction = (event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1);
        const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : Math.max(0, Math.min(buttons.length - 1, currentIndex + direction));
        buttons[nextIndex].focus({ preventScroll: true });
        buttons[nextIndex].click();
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.activeMediaId = activeMedia.dataset.mediaId;

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
            this.elements.thumbnails.resetPages?.();
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // Selecting a size or thumbnail must not pull the customer away from
          // the purchase controls. Horizontal movement is confined to the rail.
          if (this.dataset.nlGallery === 'true') return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (!this.elements.thumbnails.slider || this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        const announce = () => {
          if (this.activeMediaId && activeItem.dataset.mediaId !== this.activeMediaId) return;
          clearTimeout(this.announcementTimer);
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          this.announcementTimer = setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        if (image.complete) announce();
        else image.addEventListener('load', announce, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        // Video/3D is loaded only by its existing, explicitly activated poster
        // control. Merely choosing a thumbnail or variant never starts playback.
        if (this.dataset.nlNoAutoplay === 'true') return;
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
